import { Grid, Typography } from 'web-library';
import React from 'react';
import { Theme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { UploadedFile } from 'web-library/models/UploadedFile';
import FileBadge from './FileBadge';
import { AttachedFileType } from '../../api/candidate/config/model';
import ApplicationFileBadge from './ApplicationFileBadge';

const useStyles = makeStyles()((theme) =>
({
  attachedFilesHeader: {
    color: theme.color.text.textHeader,
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
  },
}));

interface AttachedFilesProps {
  isOpsAdmin?: boolean;
  disableDelete?: boolean;
  files: UploadedFile[];
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  onUploadFileDelete: (file: File, isDraft: boolean) => void;
  draftFiles?: UploadedFile[];
  downloadableLink?: boolean;
  isLoading?: boolean;
  headerText?: string;
  hideDelete?: boolean;
  attachedFileType?: AttachedFileType;
  hideHeader?: boolean;
}

const handleUpdateFileUploadComplete = (
  files: UploadedFile[],
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>,
  fileKey: string,
  uploadError: boolean
) => {
  setFiles((prevState) =>
    prevState.map((file) => {
      if (file.key === fileKey) {
        return {
          ...file,
          pending: false,
          hasError: uploadError,
          uploaded: !uploadError,
        };
      }
      return file;
    })
  );
};

const AttachedFiles = ({
  isOpsAdmin = false,
  disableDelete,
  files,
  setFiles,
  onUploadFileDelete,
  draftFiles,
  downloadableLink = false,
  isLoading,
  headerText,
  hideDelete,
  attachedFileType = AttachedFileType.DOCUMENT,
  hideHeader,
}: AttachedFilesProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const attachedFiles = (isDraft: boolean, fileArr?: UploadedFile[]) =>
    fileArr?.map((uploadFile, index) => {
      if (attachedFileType === AttachedFileType.DOCUMENT) {
        return (
          <FileBadge
            isOpsAdmin={isOpsAdmin}
            buttonRemoveIdx={index}
            onUpdateFileUploadComplete={handleUpdateFileUploadComplete}
            key={
              uploadFile.key ? uploadFile.key : uploadFile.file.name + uploadFile.file.size + index
            }
            hideRemoveButton={hideDelete}
            file={uploadFile.file}
            presignedUrl={uploadFile.presignedUrl}
            isLoading={isLoading ?? uploadFile.pending}
            hasError={uploadFile.hasError}
            presignKey={uploadFile.key}
            onUploadFileDelete={(file: File) => onUploadFileDelete(file, isDraft)}
            uploadedFilesHandler={{ files, setFiles }}
            downloadableLink={downloadableLink}
          />
        );
      }
      return (
        <ApplicationFileBadge
          buttonRemoveIdx={index}
          onUpdateFileUploadComplete={handleUpdateFileUploadComplete}
          key={
            uploadFile.key ? uploadFile.key : uploadFile.file.name + uploadFile.file.size + index
          }
          disableRemoveButton={!!disableDelete}
          hideRemoveButton={hideDelete}
          file={uploadFile.file}
          presignedUrl={uploadFile.presignedUrl}
          isLoading={isLoading ?? uploadFile.pending}
          hasError={uploadFile.hasError}
          presignKey={uploadFile.key}
          onUploadFileDelete={(file: File) => onUploadFileDelete(file, isDraft)}
          uploadedFilesHandler={{ files, setFiles }}
          downloadableLink={downloadableLink}
          isNewFile={!isDraft}
        />
      );
    });

  const renderHeader = () => (
    <Typography variant="body1" className={classes.attachedFilesHeader}>
      {headerText || t('accessArrangement->>SelectCondition.AttachedFiles')}
    </Typography>
  );

  return (
    <>
      {((draftFiles && draftFiles.length > 0) || (files && files.length > 0)) && (
        <Grid>
          {!hideHeader && renderHeader()}
          {attachedFiles(true, draftFiles)}
          {attachedFiles(false, files)}
        </Grid>
      )}
    </>
  );
};

export default AttachedFiles;
